import { addDays, addMonths, type LocalDate } from '../dates';
import type { Mutation } from '../mutations';
import { MAX_AMOUNT_MINOR } from '../spending/expense';
import type { BuildCtx } from '../spending/types';
import type { BudgetSettings } from '../rules/readModel';
import {
  accountEffect,
  isInterestEntry,
  receiveIntoAccount,
  usedForSpending,
  type AccountEntryLike,
  type AccountGrowth,
  type AccountLike,
  type AccountStatus,
} from './accounts';
import type { EffectivePayday } from './paydays';
import { isValidLocalDate, type IncomeSourceLike } from './schedule';
import { budgetMutation, type BudgetResult } from './types';

/**
 * v0.6 Savings & investments (Ivan, 2026-11-03): a GoTyme savings at 3% a year, a Pag-IBIG MP2
 * (yearly dividend, 5-year term), a time deposit (fixed rate, paid at maturity).
 *
 * Interest is never income: it isn't in Budget's cash flow or Safe to spend. It is shown on the
 * account as an estimate. Interest the user actually got is recorded as "Interest received", a
 * deposit (ledger transfer into the account, sourceId = the account), excluded from income and
 * spending like Coming in.
 *
 * The estimate (labelled "estimated" everywhere):
 * - interest accrues daily at rate / 365 on the account's money, from its start date (else its
 *   opening date; before the opening date the opening balance is assumed, as for a time deposit
 *   entered after it started);
 * - "monthly" and "yearly" credit the accrued interest on each month or year after the start, and
 *   credited interest earns interest after that (compounding); "maturity" pays simple interest
 *   once, at maturity;
 * - the money it accrues on is the balance without recorded interest rows (those are part of
 *   what the estimate stands for, so they are never counted twice);
 * - nothing accrues after the maturity date.
 */

export const INTEREST_NOTE = 'Interest received';

const DAYS_PER_YEAR = 365;
/** No estimate walks more than this many days (about 40 years). */
const MAX_DAYS = 40 * 366;

export interface InterestEstimate {
  rateBp: number;
  growth: AccountGrowth;
  /** Interest earned from the start date to today, estimated (credited plus accrued). */
  earnedMinor: number;
  /** What the account is worth in 12 months, estimated (capped at maturity). */
  in12MonthsMinor: number;
  /** What it is worth at maturity, estimated; null without a maturity date. */
  atMaturityMinor: number | null;
  /** Interest actually recorded as received (deposits on the account). */
  receivedMinor: number;
  matured: boolean;
}

/** When interest starts accruing. */
export function interestStart(a: Pick<AccountLike, 'startDate' | 'openingDate'>): LocalDate {
  return a.startDate ?? a.openingDate;
}

/** Credit days after `start` for monthly / yearly growth: start + k months (or k years). */
function isCreditDay(start: LocalDate, growth: AccountGrowth, d: LocalDate, k: number): boolean {
  if (growth === 'maturity') return false;
  return addMonths(start, growth === 'monthly' ? k : 12 * k) === d;
}

/**
 * Walks the estimate from the start day to `until` (exclusive), on the balance without recorded
 * interest. Returns the interest earned (credited + accrued) in centavos, as a float.
 */
function walk(
  account: AccountLike,
  entries: readonly AccountEntryLike[],
  today: LocalDate,
  until: LocalDate,
): number {
  const rate = (account.annualRateBp ?? 0) / 10_000;
  const growth = account.growth ?? 'monthly';
  const start = interestStart(account);
  const end = account.maturityDate && account.maturityDate < until ? account.maturityDate : until;
  // Daily changes to the money it accrues on: movements from the opening date to today only
  // (projections assume no new deposits or withdrawals).
  const deltas = new Map<LocalDate, number>();
  for (const e of entries) {
    if (e.deletedAt !== null || isInterestEntry(e)) continue;
    if (e.date < account.openingDate || e.date > today) continue;
    const fx = accountEffect(e, account.id);
    if (fx !== 0) deltas.set(e.date, (deltas.get(e.date) ?? 0) + fx);
  }
  let principal = account.openingBalanceMinor;
  // Movements dated before the start (but on or after the opening date) are already in.
  for (const [d, fx] of deltas) if (d < start) principal += fx;
  let credited = 0;
  let pending = 0;
  let k = 1;
  let d = start;
  for (let i = 0; d < end && i < MAX_DAYS; i++) {
    if (d >= account.openingDate) principal += deltas.get(d) ?? 0;
    if (i > 0 && isCreditDay(start, growth, d, k)) {
      credited += pending;
      pending = 0;
      k++;
    }
    const base = principal + (growth === 'maturity' ? 0 : credited);
    if (base > 0) pending += (base * rate) / DAYS_PER_YEAR;
    d = addDays(d, 1);
  }
  return credited + pending;
}

/** The balance the estimate grows from: today's balance without recorded interest rows. */
function principalToday(
  account: AccountLike,
  entries: readonly AccountEntryLike[],
  today: LocalDate,
): number {
  let sum = account.openingBalanceMinor;
  for (const e of entries) {
    if (e.deletedAt !== null || isInterestEntry(e)) continue;
    if (e.date < account.openingDate || e.date > today) continue;
    sum += accountEffect(e, account.id);
  }
  return sum;
}

/** The estimate for an account with a rate; null without one. */
export function estimateInterest(
  account: AccountLike,
  entries: readonly AccountEntryLike[],
  today: LocalDate,
): InterestEstimate | null {
  const rateBp = account.annualRateBp ?? 0;
  if (rateBp <= 0) return null;
  const growth = account.growth ?? 'monthly';
  const mine = entries.filter((e) => e.accountId === account.id || e.toAccountId === account.id);
  const principal = principalToday(account, mine, today);
  const valueAt = (d: LocalDate) => principal + Math.round(walk(account, mine, today, d));
  const in12 = addMonths(today, 12);
  const receivedMinor = mine
    .filter((e) => e.deletedAt === null && isInterestEntry(e) && e.toAccountId === account.id)
    .reduce((s, e) => s + e.amountMinor, 0);
  return {
    rateBp,
    growth,
    earnedMinor: Math.round(walk(account, mine, today, today)),
    in12MonthsMinor: valueAt(in12),
    atMaturityMinor: account.maturityDate ? valueAt(account.maturityDate) : null,
    receivedMinor,
    matured: account.maturityDate != null && account.maturityDate <= today,
  };
}

/** "Savings": the savings-only accounts' total and their estimated interest so far. */
export interface SavingsSummary {
  accounts: AccountStatus[];
  totalMinor: number;
  /** Estimated interest earned so far, all savings-only accounts with a rate. */
  interestMinor: number;
}

export function savingsSummary(
  statuses: readonly AccountStatus[],
  entries: readonly AccountEntryLike[],
  today: LocalDate,
): SavingsSummary {
  const accounts = statuses.filter((a) => !usedForSpending(a.account));
  return {
    accounts,
    totalMinor: accounts.reduce((s, a) => s + a.balanceMinor, 0),
    interestMinor: accounts.reduce(
      (s, a) => s + (estimateInterest(a.account, entries, today)?.earnedMinor ?? 0),
      0,
    ),
  };
}

// ---------- Builders ----------

/** "Use for spending" on or off, editable any time (one update). */
export function buildSetUseForSpending(
  account: Pick<AccountLike, 'id' | 'useForSpending'>,
  on: boolean,
  ctx: BuildCtx,
): Mutation | null {
  if (usedForSpending(account) === on) return null;
  return budgetMutation(ctx, 'money_accounts', 'update', account.id, { useForSpending: on });
}

export type InterestError = 'not_found' | 'bad_amount' | 'bad_date';

/**
 * "Interest received" (a credited interest or an MP2 dividend): a deposit into the account, never
 * income. sourceId = the account, so it reads as interest (isInterestEntry).
 */
export function buildRecordInterest(
  account: Pick<AccountLike, 'id' | 'deletedAt'>,
  got: { amountMinor: number; date: LocalDate },
  ctx: BuildCtx,
): BudgetResult<{ id: string; mutation: Mutation }, InterestError> {
  if (account.deletedAt !== null) return { ok: false, error: 'not_found' };
  if (
    !Number.isInteger(got.amountMinor) ||
    got.amountMinor <= 0 ||
    got.amountMinor > MAX_AMOUNT_MINOR
  )
    return { ok: false, error: 'bad_amount' };
  if (!isValidLocalDate(got.date)) return { ok: false, error: 'bad_date' };
  const id = ctx.newId();
  return {
    ok: true,
    id,
    mutation: budgetMutation(ctx, 'ledger_entries', 'insert', id, {
      date: got.date,
      amountMinor: got.amountMinor,
      currency: 'PHP',
      kind: 'transfer',
      categoryId: null,
      method: null,
      fundId: null,
      accountId: null,
      toAccountId: account.id,
      sourceModule: 'budget',
      sourceId: account.id,
      note: INTEREST_NOTE,
      recordedTz: ctx.tz,
    }),
  };
}

// ---------- Straight to savings ----------

/**
 * v0.6 "Goes to" a savings account: ids of the paydays and one-off income rows that land in a
 * savings-only account. A received payday lands where its ledger row says; an expected one where
 * its source sends it (else Budget's "Receive into"). The cash flow shows these as "To savings"
 * and Safe to spend leaves them out (the money isn't spendable).
 */
export function incomeToSavings(input: {
  paydays: readonly Pick<EffectivePayday, 'id' | 'sourceId' | 'ledgerEntryId'>[];
  /**
   * The ledger rows in range (all of them, not just ones that move an account): a payday's row
   * that is found with no account lands nowhere, so it is never "to savings".
   */
  entries: readonly (Pick<AccountEntryLike, 'id' | 'kind' | 'deletedAt'> & {
    accountId?: string | null;
  })[];
  sources: readonly Pick<IncomeSourceLike, 'id' | 'accountId'>[];
  settings: Pick<BudgetSettings, 'incomeAccountId'>;
  accounts: readonly AccountLike[];
}): Set<string> {
  const savings = new Set(
    input.accounts.filter((a) => a.deletedAt === null && !usedForSpending(a)).map((a) => a.id),
  );
  const out = new Set<string>();
  if (savings.size === 0) return out;
  const byId = new Map(input.entries.map((e) => [e.id, e]));
  const paydayRows = new Set<string>();
  for (const p of input.paydays) {
    const row = p.ledgerEntryId ? byId.get(p.ledgerEntryId) : undefined;
    if (p.ledgerEntryId) paydayRows.add(p.ledgerEntryId);
    const lands = row
      ? (row.accountId ?? null)
      : receiveIntoAccount(
          null,
          input.sources.find((s) => s.id === p.sourceId),
          input.settings,
          true,
          input.accounts,
        );
    if (lands && savings.has(lands)) out.add(p.id);
  }
  for (const e of input.entries) {
    if (e.deletedAt !== null || e.kind !== 'income' || paydayRows.has(e.id)) continue;
    if (e.accountId && savings.has(e.accountId)) out.add(e.id);
  }
  return out;
}
